import { useMemo, useState } from 'react';
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';

// .pen: "linha patrimonial" / "ds/chart/donut · despesas por grupo" /
// "barras por dia" (dentro da secção "acc · Gráficos", Camada 4)
import pt from '../../locales/pt';
import { cn } from '../../utils/cn';
import { useChartTheme } from '../../utils/chartTheme';
import { formatEUR } from '../../utils/money';
import { ReportDonut } from './report/ReportDonut';
import { ReportPanel } from './report/ReportPrimitives';

const EXPENSE_TABS = {
  group: 'group',
  category: 'category',
};

function ReportExpenseDistribution({ charts, totalEur }) {
  const hasGroup = (charts?.by_group?.length || 0) > 0;
  const hasCategory = (charts?.by_category?.length || 0) > 0;
  const defaultTab = hasGroup ? EXPENSE_TABS.group : EXPENSE_TABS.category;
  const [tab, setTab] = useState(defaultTab);

  if (!hasGroup && !hasCategory) return null;

  const activeTab = hasGroup && hasCategory ? tab : defaultTab;
  const activeData =
    activeTab === EXPENSE_TABS.group ? charts.by_group : charts.by_category;
  const hint =
    activeTab === EXPENSE_TABS.group
      ? pt.snapshots.report.charts.expensesByGroupHint
      : pt.snapshots.report.charts.expensesByCategoryHint;

  return (
    <ReportPanel>
      <div className="flex flex-col gap-space-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <p className="text-type-body font-semibold text-foreground">
            {pt.snapshots.report.charts.distributionTitle}
          </p>
          <p className="mt-0.5 text-type-body-sm text-muted-foreground">{hint}</p>
        </div>
        {hasGroup && hasCategory ? (
          <div
            className="flex shrink-0 rounded-md border border-border bg-background/80 p-0.5 text-xs font-medium"
            role="tablist"
            aria-label={pt.snapshots.report.charts.distributionTabsLabel}
          >
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === EXPENSE_TABS.group}
              className={cn(
                'rounded px-3 py-1.5 transition-colors',
                activeTab === EXPENSE_TABS.group
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-muted-foreground',
              )}
              onClick={() => setTab(EXPENSE_TABS.group)}
            >
              {pt.snapshots.report.charts.tabByGroup}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === EXPENSE_TABS.category}
              className={cn(
                'rounded px-3 py-1.5 transition-colors',
                activeTab === EXPENSE_TABS.category
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-muted-foreground',
              )}
              onClick={() => setTab(EXPENSE_TABS.category)}
            >
              {pt.snapshots.report.charts.tabByCategory}
            </button>
          </div>
        ) : null}
      </div>
      <div className="mt-6">
        <ReportDonut
          key={activeTab}
          data={activeData}
          centerLabel={pt.snapshots.report.charts.totalSpent}
          centerValue={formatEUR(totalEur)}
          emptyLabel={pt.snapshots.report.charts.empty}
        />
      </div>
    </ReportPanel>
  );
}

function formatDayLabel(isoDate) {
  const d = new Date(`${isoDate}T12:00:00`);
  return d.toLocaleDateString('pt-PT', { day: 'numeric', month: 'short' });
}

function formatPercent(value) {
  if (value == null || !Number.isFinite(Number(value))) return null;
  return `${Math.round(Number(value))}%`;
}

/**
 * Gráficos do relatório — layout editorial (painéis amplos, legendas completas).
 */
export function SelfieReportCharts({ charts, timeline }) {
  const chart = useChartTheme();
  const dailyData = useMemo(
    () =>
      (charts?.by_day || []).map((row) => ({
        ...row,
        shortLabel: row.label,
        isPeak: (charts?.peak_days || []).some((p) => p.date === row.date),
      })),
    [charts?.by_day, charts?.peak_days],
  );

  const timelineData = useMemo(
    () =>
      (timeline || []).map((row, idx, arr) => ({
        label: row.label,
        patrimonio: Number(row.patrimonio_eur || 0),
        isLast: idx === arr.length - 1,
      })),
    [timeline],
  );

  const peakDays = charts?.peak_days || [];
  const stats = charts?.stats;
  const hasExpenses = (stats?.total_eur || 0) > 0;
  return (
    <>
      {timelineData.length > 1 ? (
        <ReportPanel className="mb-space-5">
          <p className="mb-space-4 text-type-section font-bold uppercase tracking-[0.08em] text-muted-foreground">
            {pt.snapshots.report.charts.patrimonioLine}
          </p>
          <div className="h-[220px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart accessibilityLayer={false} data={timelineData} margin={{ top: 12, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="2 4" stroke={chart.grid} vertical={false} />
                <XAxis
                  dataKey="label"
                  stroke={chart.axis}
                  fontSize={11}
                  fontFamily="ui-monospace, monospace"
                  tickLine={false}
                  axisLine={{ stroke: chart.axisLine }}
                />
                <YAxis
                  stroke={chart.axis}
                  fontSize={10}
                  fontFamily="ui-monospace, monospace"
                  width={44}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => `${Math.round(v / 1000)}k`}
                />
                <Area
                  type="monotone"
                  dataKey="patrimonio"
                  fill={chart.primary}
                  fillOpacity={0.12}
                  stroke="none"
                />
                <Line
                  type="monotone"
                  dataKey="patrimonio"
                  stroke={chart.primaryDark}
                  strokeWidth={2.5}
                  dot={({ cx, cy, payload }) =>
                    payload?.isLast ? (
                      <circle cx={cx} cy={cy} r={6} fill={chart.dotInner} stroke={chart.dotOuter} strokeWidth={3} />
                    ) : (
                      <circle cx={cx} cy={cy} r={4} fill={chart.dotOuter} stroke={chart.primaryDark} strokeWidth={2} />
                    )
                  }
                  activeDot={{ r: 6 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </ReportPanel>
      ) : null}

      {hasExpenses ? (
        <div className="flex flex-col gap-space-5">
          <ReportExpenseDistribution charts={charts} totalEur={stats.total_eur} />

          {dailyData.length > 0 ? (
            <ReportPanel>
              <p className="text-type-body font-semibold text-foreground">{pt.snapshots.report.charts.expensesByDay}</p>
              <p className="mt-0.5 text-type-body-sm text-muted-foreground">
                {pt.snapshots.report.charts.expensesByDayHint
                  .replace('{days}', String(stats.days_with_expenses || 0))
                  .replace('{total}', String(stats.cycle_days || dailyData.length))}
              </p>
              <div className="mt-space-4 h-56 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    accessibilityLayer={false}
                    data={dailyData}
                    margin={{ top: 8, right: 4, left: 0, bottom: 4 }}
                    barCategoryGap="18%"
                  >
                    <CartesianGrid strokeDasharray="2 4" stroke={chart.grid} vertical={false} />
                    <XAxis
                      dataKey="shortLabel"
                      stroke={chart.axis}
                      fontSize={11}
                      fontFamily="ui-monospace, monospace"
                      tickLine={false}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      stroke={chart.axis}
                      fontSize={10}
                      width={48}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Bar
                      dataKey="total"
                      name={pt.snapshots.report.table.spent}
                      radius={[3, 3, 0, 0]}
                      fill={chart.primary}
                      activeBar={{ fill: chart.primaryDark }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {peakDays.length > 0 ? (
                <div className="mt-6 border-t border-border/80 pt-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                    {pt.snapshots.report.charts.peakDaysTitle}
                  </p>
                  <ul className="mt-3 flex flex-col gap-2">
                    {peakDays.map((day, idx) => (
                      <li
                        key={day.date}
                        className="flex items-center justify-between gap-4 text-sm"
                      >
                        <span className="text-muted-foreground">
                          <span className="mr-2 font-mono text-xs text-muted-foreground/70">
                            {String(idx + 1).padStart(2, '0')}
                          </span>
                          {formatDayLabel(day.date)}
                        </span>
                        <span className="font-medium tabular-nums text-foreground">
                          {formatEUR(day.total)}
                          {stats?.total_eur > 0 ? (
                            <span className="ml-2 text-xs font-normal text-muted-foreground">
                              ({formatPercent((day.total / stats.total_eur) * 100)})
                            </span>
                          ) : null}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </ReportPanel>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
